import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useStatuteApi } from '../services/api/useStatute.api';
import { StatuteStatistic, StatuteStatisticsResponse } from '../types/StatuteStatistics.type';
import { useDebouncedValue } from './useDebouncedValue';

interface UseCombinedStatisticsParams {
  caseId: number | undefined;
  baseStatuteIds: number[];
  inclusionGroups: number[][];
  exclusionGroups: number[][];
  countyFips: string | undefined;
  enabled: boolean;
}

interface UseCombinedStatisticsResult {
  combinedStatistics: StatuteStatistic[] | null;
  statisticsData: StatuteStatisticsResponse | undefined;
  isLoading: boolean;
}

// debounce for removing rows
const STATISTICS_DEBOUNCE_MS = 400;

// Get statistics for the union of the base statutes and inclusion pools minus the exclusion pools.
export function useCombinedStatistics({
  caseId,
  baseStatuteIds,
  inclusionGroups,
  exclusionGroups,
  countyFips,
  enabled,
}: UseCombinedStatisticsParams): UseCombinedStatisticsResult {
  const { getStatuteStatistics, getGroupsStatuteStatistics } = useStatuteApi();

  const inclusion = useMemo(() => inclusionGroups.filter((ids) => ids.length > 0), [inclusionGroups]);
  const exclusion = useMemo(() => exclusionGroups.filter((ids) => ids.length > 0), [exclusionGroups]);

  // Debounce the inputs on bulk add/remove into one fetch
  const inputs = useMemo(() => ({ baseStatuteIds, inclusion, exclusion }), [baseStatuteIds, inclusion, exclusion]);
  const debounced = useDebouncedValue(inputs, STATISTICS_DEBOUNCE_MS);
  const { baseStatuteIds: base, inclusion: debouncedInclusion, exclusion: debouncedExclusion } = debounced;

  const hasGroups = debouncedInclusion.length > 0 || debouncedExclusion.length > 0;
  const queryEnabled = enabled && !!countyFips && base.length > 0 && (!hasGroups || caseId != null);

  const { data, isLoading } = useQuery({
    queryKey: ['statuteStatistics', countyFips, caseId, base, debouncedInclusion, debouncedExclusion],
    queryFn: () =>
      hasGroups
        ? getGroupsStatuteStatistics({
            case_id: caseId!,
            county_fips: countyFips!,
            statute_ids: base,
            inclusion_groups_ids: debouncedInclusion.length > 0 ? debouncedInclusion : undefined,
            exclusion_groups_ids: debouncedExclusion.length > 0 ? debouncedExclusion : undefined,
          })
        : getStatuteStatistics({ county_fips: countyFips!, statute_ids: base }),
    enabled: queryEnabled,
  });

  const combinedStatistics = data?.statute_statistics ?? null;

  // Format statisticsData for the heatmap and Cases matching criteria box
  const statisticsData = useMemo<StatuteStatisticsResponse | undefined>(() => {
    if (!combinedStatistics || !countyFips) return undefined;
    return {
      county_fips: countyFips,
      statute_statistics: combinedStatistics,
      statutes_ids: [],
    };
  }, [combinedStatistics, countyFips]);

  return { combinedStatistics, statisticsData, isLoading: queryEnabled && isLoading };
}
